/**
 * The staff register and project crew (MB decision 422, Q365; docs/specs/cloud/staff-register.md).
 *
 * - Every tenant has one staff register: the `person` table. A person has one or more roles (Pilot, Operator, Office
 *   staff, Processor, Manager) and a free-text title ("CEO"). Someone who leaves is made inactive. A tenant admin or
 *   a manager may delete a person (MB, 25 Oct 2026; decision 601): a soft delete (`deleted_at`), so the records that
 *   name them keep the name; a deleted person is in no list for new records and is marked "(deleted)" on old ones.
 *   After `PERSON_BIN_DAYS` the person is removed for good (decision 602): the daily entries and crew rows that named
 *   them keep the name as text (`pilot_name`, …, `person_name`), read with `entryCrewName` and `crewRowName`.
 * - Every pilot and operator field in Teslyn is a drop-down from the register (`crewChoices`): pilot fields list
 *   Pilots, operator fields Operators; inactive people are hidden from new choices and still shown on old records.
 * - Crew on projects (`ProjectCrew`) is chosen by managers and office staff; the Operations Hub shows it on the
 *   project cards (`crewCardLines`).
 * - Names are matched without regard to case or repeated spaces (`findPersonByName`), so a TJR import never adds a
 *   second "Kyle Gardner".
 *
 * Who may change the register is enforced by RLS (supabase/migrations/20261021020000_staff_register.sql); the apps
 * use `canEditStaffRegister` only to show or hide controls.
 */
import { crewOnDate } from './crew-dates.js';
import { lifecycleDateText } from './project-lifecycle.js';
import type {
  DailyEntry,
  IsoDate,
  Person,
  ProjectCrew,
  RegisterRole,
  Uuid,
  ValidationIssue,
} from './model.js';

/** The register roles in the order lists, chips and tick boxes use (decision 422). */
export const REGISTER_ROLES: readonly RegisterRole[] = [
  'pilot',
  'operator',
  'office_staff',
  'processor',
  'manager',
] as const;

export const REGISTER_ROLE_LABEL: Readonly<Record<RegisterRole, string>> = {
  pilot: 'Pilot',
  operator: 'Operator',
  office_staff: 'Office staff',
  processor: 'Processor',
  manager: 'Manager',
};

const REGISTER_ROLE_PLURAL: Readonly<Record<RegisterRole, string>> = {
  pilot: 'Pilots',
  operator: 'Operators',
  office_staff: 'Office staff',
  processor: 'Processors',
  manager: 'Managers',
};

/** The roles a pilot or an operator field lists. */
export type CrewRole = Extract<RegisterRole, 'pilot' | 'operator'>;

/** The longest title the register keeps. */
export const MAX_TITLE_LENGTH = 80;

const isRegisterRole = (v: string): v is RegisterRole => (REGISTER_ROLES as readonly string[]).includes(v);

/** The person's roles in register order; a row from before the register has only its old single role. */
export function personRoles(p: Pick<Person, 'role' | 'roles'>): RegisterRole[] {
  const held = new Set<string>(p.roles && p.roles.length > 0 ? p.roles : [p.role]);
  return REGISTER_ROLES.filter((r) => held.has(r));
}

export function holdsRole(p: Pick<Person, 'role' | 'roles'>, role: RegisterRole): boolean {
  return personRoles(p).includes(role);
}

/** "Pilot, Manager". */
export function registerRolesLabel(p: Pick<Person, 'role' | 'roles'>): string {
  return personRoles(p)
    .map((r) => REGISTER_ROLE_LABEL[r])
    .join(', ');
}

/**
 * The single `role` column older Teslyn Field builds read (pilot, operator or manager): pilot when the person
 * flies, else operator, else manager. The database derives the same (`person_register_roles`, the Postgres trigger),
 * so an old laptop always gets a value its schema accepts.
 */
export function legacyPersonRole(roles: readonly string[]): Person['role'] {
  if (roles.includes('pilot')) return 'pilot';
  if (roles.includes('operator')) return 'operator';
  return 'manager';
}

/** Roles as the register stores them: known ones only, once each, in register order. */
export function normaliseRoles(roles: readonly string[]): RegisterRole[] {
  const set = new Set(roles.filter(isRegisterRole));
  return REGISTER_ROLES.filter((r) => set.has(r));
}

/** Not removed and not made inactive. */
export function isActivePerson(p: Pick<Person, 'active' | 'deleted_at'>): boolean {
  return p.deleted_at === null && p.active !== false;
}

/** The name as the register keeps it: trimmed, single spaces. */
export function normalisePersonName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

/** The key two names are compared by: normalised and lower case. */
export function personNameKey(name: string): string {
  return normalisePersonName(name).toLowerCase();
}

/**
 * The deleted register person with this name (any case or spacing), the most recently deleted first, when no live
 * person has it: an import that names them matches them instead of adding them again (decision 604, Q531 B), and
 * "Add person" with their name offers to restore them instead (decision 663, Q588 A; {@link deletedNameOffer}).
 */
export function findDeletedPersonByName<P extends Pick<Person, 'name' | 'deleted_at'>>(
  people: readonly P[],
  name: string,
): P | undefined {
  if (findPersonByName(people, name)) return undefined;
  const key = personNameKey(name);
  return people
    .filter((p) => p.deleted_at !== null && personNameKey(p.name) === key)
    .sort((a, b) => (b.deleted_at ?? '').localeCompare(a.deleted_at ?? ''))[0];
}

/** What an import says of a name it matched to a deleted person (decision 604; MB's words). */
export function deletedPersonImportNote(name: string): string {
  return `${name} was deleted from the staff register; a tenant admin or manager can restore them`;
}

/** The register person with this name (any case or spacing), active or not; never a removed row. */
export function findPersonByName<P extends Pick<Person, 'name' | 'deleted_at'>>(
  people: readonly P[],
  name: string,
): P | undefined {
  const key = personNameKey(name);
  return people.find((p) => p.deleted_at === null && personNameKey(p.name) === key);
}

/**
 * The words of that offer: in Teslyn Cloud's People page, Staff register (`cloud`) or Teslyn Field's Project settings (`field`, where
 * the restore itself is done in Teslyn Cloud), for someone who may restore (tenant admins and managers,
 * {@link canDeleteStaff}) or not.
 */
export function deletedNameOffer(
  p: Pick<Person, 'name' | 'deleted_at'>,
  canRestore: boolean,
  where: 'cloud' | 'field' = 'cloud',
): string {
  const deleted = `${p.name} was deleted from the staff register${where === 'field' ? ' in Teslyn Cloud' : ''} on ${lifecycleDateText(p.deleted_at ?? '')}.`;
  const records = 'their roles, title and the records that name them';
  if (where === 'field')
    return canRestore
      ? `${deleted} Restore them instead of adding a new person? They come back with ${records}, here after the next sync.`
      : `${deleted} A tenant admin or manager can restore them in Teslyn Cloud (People, Deleted people), with ${records}.`;
  return canRestore
    ? `${deleted} Restore them instead of adding a new person? They come back with ${records}.`
    : `${deleted} A tenant admin or manager can restore them under Deleted people, with ${records}.`;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A new or edited register person: a name not already in the register, one role or more, a short title. */
export function validatePerson(person: Person, existing: readonly Person[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const name = normalisePersonName(person.name);
  if (name === '') issues.push({ field: 'name', message: "Enter the person's name." });
  else {
    const other = findPersonByName(
      existing.filter((p) => p.id !== person.id),
      name,
    );
    if (other) {
      issues.push({
        field: 'name',
        message:
          other.active === false
            ? `${other.name} is already in the staff register (inactive). Make them active again instead.`
            : `${other.name} is already in the staff register.`,
      });
    }
  }
  if (normaliseRoles(person.roles ?? [person.role]).length === 0) {
    issues.push({ field: 'roles', message: 'Choose at least one role.' });
  }
  if ((person.title ?? '').trim().length > MAX_TITLE_LENGTH) {
    issues.push({ field: 'title', message: `Keep the title to ${MAX_TITLE_LENGTH} characters or fewer.` });
  }
  const email = (person.email ?? '').trim();
  if (email !== '' && !EMAIL.test(email)) {
    issues.push({
      field: 'email',
      message: 'Enter an email address such as name@example.com, or leave it blank.',
    });
  }
  return issues;
}

// ---------------------------------------------------------------- drop-downs

/** Where a choice sits in the drop-down: on this project, elsewhere in the register, or kept for an old record. */
export type CrewChoiceGroup = 'project' | 'register' | 'former';

export interface CrewChoice {
  readonly id: Uuid;
  readonly label: string;
  readonly group: CrewChoiceGroup;
}

export const CREW_CHOICE_GROUP_LABEL: Readonly<Record<CrewChoiceGroup, string>> = {
  project: 'On this project',
  register: 'Staff register',
  former: 'On this record only',
};

const byName = (a: { readonly label: string }, b: { readonly label: string }): number =>
  a.label.localeCompare(b.label, 'en-AU');

/**
 * The choices of a pilot field (`role` pilot) or an operator field (operator): the register's active people holding
 * the role, by name, the project's crew on `date` first when `crew` is given. `current` (the value an existing record
 * holds) is always listed, at the end when it is no longer a choice for new records, with the reason in its label.
 */
export function crewChoices(
  people: readonly Person[],
  role: CrewRole,
  current: Uuid | null | undefined,
  project?: { readonly crew: readonly ProjectCrew[]; readonly date: IsoDate | null },
): CrewChoice[] {
  const onProject = new Set(
    project
      ? (project.date
          ? crewOnDate(project.crew, project.date)
          : project.crew.filter((c) => c.deleted_at === null)
        )
          .filter((c) => c.role === role)
          .map((c) => c.person_id)
      : [],
  );
  const choices: CrewChoice[] = people
    .filter((p) => isActivePerson(p) && holdsRole(p, role))
    .map((p): CrewChoice => ({
      id: p.id,
      label: p.name,
      group: onProject.has(p.id) ? 'project' : 'register',
    }))
    .sort((a, b) => (a.group === b.group ? byName(a, b) : a.group === 'project' ? -1 : 1));
  if (current && !choices.some((c) => c.id === current)) {
    const p = people.find((x) => x.id === current);
    const label =
      !p || isActivePerson(p)
        ? p
          ? `${p.name} (no longer ${role === 'pilot' ? 'a pilot' : 'an operator'})`
          : 'Not in the staff register'
        : crewPersonName(people, current);
    choices.push({ id: current, label, group: 'former' });
  }
  return choices;
}

/**
 * A register person's name in a list of records (a crew list, an old entry): the name, "(inactive)" for someone who
 * left, "(deleted)" for someone a tenant admin deleted, or "Not in the staff register" for an id the laptop or the
 * page does not hold.
 */
export function crewPersonName(
  people: readonly Pick<Person, 'id' | 'name' | 'active' | 'deleted_at'>[],
  id: Uuid,
): string {
  const p = people.find((x) => x.id === id);
  if (!p) return 'Not in the staff register';
  if (p.deleted_at !== null) return `${p.name} (deleted)`;
  return p.active === false ? `${p.name} (inactive)` : p.name;
}

/** "Terry Miller (removed from the staff register)": a name kept as text after the person was removed for good. */
export function removedPersonLabel(name: string): string {
  return `${name} (removed from the staff register)`;
}

/**
 * A project crew row's name in a crew list: its register person as `crewPersonName` says, or, once the person was
 * removed for good (decision 602; `person_id` null), the name the row kept.
 */
export function crewRowName(
  people: readonly Pick<Person, 'id' | 'name' | 'active' | 'deleted_at'>[],
  row: Pick<ProjectCrew, 'person_id' | 'person_name'>,
): string {
  if (row.person_id === null) return removedPersonLabel(row.person_name ?? 'Someone');
  if (row.person_name && !people.some((p) => p.id === row.person_id))
    return removedPersonLabel(row.person_name);
  return crewPersonName(people, row.person_id);
}

// ---------------------------------------------------------------- a daily entry's crew slots

/** The four crew slots of a daily entry. */
export type EntryCrewSlot = 'pilot' | 'pilot2' | 'operator1' | 'operator2';

export const ENTRY_CREW_SLOTS: readonly EntryCrewSlot[] = ['pilot', 'pilot2', 'operator1', 'operator2'];

/** The role a slot is filled from. */
export const CREW_SLOT_ROLE: Readonly<Record<EntryCrewSlot, CrewRole>> = {
  pilot: 'pilot',
  pilot2: 'pilot',
  operator1: 'operator',
  operator2: 'operator',
};

/** The id column of a slot (`pilot_id`) and the column that keeps the name once the person is removed (`pilot_name`). */
export const crewSlotId = (slot: EntryCrewSlot): `${EntryCrewSlot}_id` => `${slot}_id`;
export const crewSlotName = (slot: EntryCrewSlot): `${EntryCrewSlot}_name` => `${slot}_name`;

/**
 * The column that keeps a pilot slot's rank with the name once the person is removed (`pilot_rank`; decision 662);
 * null for the operator slots, whose line prints no rank.
 */
export const crewSlotRank = (slot: EntryCrewSlot): 'pilot_rank' | 'pilot2_rank' | null =>
  slot === 'pilot' ? 'pilot_rank' : slot === 'pilot2' ? 'pilot2_rank' : null;

type EntryCrew = Pick<DailyEntry, 'pilot_id' | 'pilot2_id' | 'operator1_id' | 'operator2_id'> &
  Partial<
    Pick<
      DailyEntry,
      'pilot_name' | 'pilot2_name' | 'operator1_name' | 'operator2_name' | 'pilot_rank' | 'pilot2_rank'
    >
  >;

/**
 * The name in a slot of a daily entry: the register person's name (deleted people too), or the name the entry kept
 * when the person was removed for good (decision 602); null when the slot is empty or names no one this side knows.
 */
export function entryCrewName(
  people: readonly Pick<Person, 'id' | 'name'>[],
  entry: EntryCrew,
  slot: EntryCrewSlot,
): string | null {
  const id = entry[crewSlotId(slot)] ?? null;
  const kept = entry[crewSlotName(slot)] ?? null;
  const p = id ? people.find((x) => x.id === id) : undefined;
  if (p) return p.name;
  return kept && kept.trim() !== '' ? kept : null;
}

/** "M Brayne": initial and surname, as TJR's Daily Log prints operators. */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name.trim();
  return `${parts[0]!.charAt(0)} ${parts.slice(1).join(' ')}`;
}

// ---------------------------------------------------------------- removed for good (decision 602)

/** Days a deleted person stays restorable before they are removed for good, as a deleted project (decision 602). */
export const PERSON_BIN_DAYS = 30;

/** When a person deleted at `deletedAt` is removed for good (ISO). */
export function personPurgeAfter(deletedAt: string): string {
  return new Date(Date.parse(deletedAt) + PERSON_BIN_DAYS * 86_400_000).toISOString();
}

/** "24 Nov 2026" and "removed for good in 29 days" for the Deleted people list. */
export function personRemovalText(
  deletedAt: string,
  now: Date = new Date(),
): { readonly date: string; readonly inWords: string } {
  const at = personPurgeAfter(deletedAt);
  const ms = Date.parse(at) - now.getTime();
  const days = Math.ceil(ms / 86_400_000);
  const inWords =
    ms <= 0
      ? 'removed for good within the hour'
      : days <= 1
        ? 'removed for good within a day'
        : `removed for good in ${days} days`;
  return { date: lifecycleDateText(at), inWords };
}

// ---------------------------------------------------------------- project crew

// crewOnDate lives in crew-dates.ts (no imports) for the Edge Functions' build
export { crewOnDate };

const overlaps = (
  a: Pick<ProjectCrew, 'start_date' | 'end_date'>,
  b: Pick<ProjectCrew, 'start_date' | 'end_date'>,
): boolean =>
  (a.start_date === null || b.end_date === null || a.start_date <= b.end_date) &&
  (b.start_date === null || a.end_date === null || b.start_date <= a.end_date);

/**
 * A new or edited crew row: a register person holding the role, active when they are newly added, an end date on or
 * after the start, and not the same person in the same role over the same days twice.
 */
export function validateProjectCrew(
  row: ProjectCrew,
  people: readonly Person[],
  existing: readonly ProjectCrew[],
): ValidationIssue[] {
  const p = people.find((x) => x.id === row.person_id && x.deleted_at === null);
  if (!row.person_id || !p)
    return [{ field: 'person_id', message: 'Choose a person from the staff register.' }];
  const issues: ValidationIssue[] = [];
  const known = existing.find((c) => c.id === row.id && c.deleted_at === null);
  if (!holdsRole(p, row.role)) {
    issues.push({
      field: 'role',
      message: `${p.name} is not a ${REGISTER_ROLE_LABEL[row.role]} in the staff register.`,
    });
  } else if (p.active === false && (!known || known.person_id !== row.person_id)) {
    issues.push({ field: 'person_id', message: `${p.name} is inactive in the staff register.` });
  }
  if (row.start_date !== null && row.end_date !== null && row.end_date < row.start_date) {
    issues.push({ field: 'end_date', message: 'The end date is before the start date.' });
  } else if (
    existing.some(
      (c) =>
        c.id !== row.id &&
        c.deleted_at === null &&
        c.person_id === row.person_id &&
        c.role === row.role &&
        overlaps(c, row),
    )
  ) {
    issues.push({
      field: 'person_id',
      message: `${p.name} is already on this project as ${REGISTER_ROLE_LABEL[row.role]} for those dates.`,
    });
  }
  return issues;
}

/** One line of a project card's crew: "Pilots: Kyle Gardner, Rhys Bowditch". */
export interface CrewLine {
  readonly role: RegisterRole;
  readonly label: string;
  readonly names: readonly string[];
}

/** A crew row with its person's name, as `project_crew_named` (Teslyn Cloud) returns it. */
export interface NamedCrew {
  readonly role: RegisterRole;
  readonly name: string;
  readonly start_date: IsoDate | null;
  readonly end_date: IsoDate | null;
  readonly deleted_at?: string | null;
}

/** The crew on a project on `date`, role by role in register order, names in order, each name once per role. */
export function crewCardLines(rows: readonly NamedCrew[], date: IsoDate): CrewLine[] {
  const now = crewOnDate(rows, date);
  return REGISTER_ROLES.flatMap((role): CrewLine[] => {
    const names = [...new Set(now.filter((r) => r.role === role).map((r) => r.name))].sort((a, b) =>
      a.localeCompare(b, 'en-AU'),
    );
    if (names.length === 0) return [];
    return [
      { role, label: names.length > 1 ? REGISTER_ROLE_PLURAL[role] : REGISTER_ROLE_LABEL[role], names },
    ];
  });
}

/** "Pilots: Kyle Gardner, Rhys Bowditch · Operator: Michael Brayne", or "No crew assigned". */
export function crewCardText(lines: readonly CrewLine[]): string {
  if (lines.length === 0) return 'No crew assigned';
  return lines.map((l) => `${l.label}: ${l.names.join(', ')}`).join(' · ');
}

/** One line of a report's crew list: a person in one role. */
export interface ReportCrewMember {
  readonly person: Person;
  readonly role: RegisterRole;
}

/**
 * The crew a project's report names as at `date` (the daily report's "Survey Crew", TJR's job crew lists): the
 * project crew who started on or before the date, and anyone the daily entries up to the date name in a crew slot
 * (pilots as Pilot, operators as Operator), each person once per role, in register order then by name. Never the
 * rest of the staff register.
 */
export function reportCrew(
  people: readonly Person[],
  crew: readonly ProjectCrew[],
  entries: readonly (Pick<DailyEntry, 'date' | 'deleted_at'> & EntryCrew)[],
  date: IsoDate,
): ReportCrewMember[] {
  // a deleted person who flew keeps their name on the report; one only on the crew list is left out (MB, 25 Oct 2026)
  const byId = new Map(people.map((p) => [p.id, p]));
  const seen = new Set<string>();
  const out: ReportCrewMember[] = [];
  const push = (person: Person, key: string, role: RegisterRole): void => {
    if (seen.has(`${key}:${role}`)) return;
    seen.add(`${key}:${role}`);
    out.push({ person, role });
  };
  const add = (id: Uuid | null | undefined, role: RegisterRole): void => {
    const person = id ? byId.get(id) : undefined;
    if (person) push(person, person.id, role);
  };
  // a person removed for good (decision 602): the name the entry kept, once per name and role, with the rank the
  // entry kept for a pilot (decision 662)
  const addKept = (
    name: string | null | undefined,
    role: CrewRole,
    tenantId: Uuid,
    rank: string | null | undefined,
  ): void => {
    if (!name || name.trim() === '') return;
    const key = `removed:${personNameKey(name)}`;
    push(removedPerson(key, normalisePersonName(name), role, tenantId, rank?.trim() || null), key, role);
  };
  for (const c of crew) {
    if (
      c.deleted_at === null &&
      c.person_id !== null &&
      (c.start_date === null || c.start_date <= date) &&
      byId.get(c.person_id)?.deleted_at === null
    )
      add(c.person_id, c.role);
  }
  const tenantId = people[0]?.tenant_id ?? '';
  for (const e of entries) {
    if (e.deleted_at !== null || e.date > date) continue;
    for (const slot of ENTRY_CREW_SLOTS) {
      const id = e[crewSlotId(slot)] ?? null;
      if (id && byId.has(id)) add(id, CREW_SLOT_ROLE[slot]);
      else {
        const rankCol = crewSlotRank(slot);
        addKept(e[crewSlotName(slot)], CREW_SLOT_ROLE[slot], tenantId, rankCol ? e[rankCol] : null);
      }
    }
  }
  const rank = (r: RegisterRole): number => REGISTER_ROLES.indexOf(r);
  return out.sort(
    (a, b) => rank(a.role) - rank(b.role) || a.person.name.localeCompare(b.person.name, 'en-AU'),
  );
}

/** A stand-in register row for a name kept after its person was removed for good (reports only). */
function removedPerson(
  id: string,
  name: string,
  role: CrewRole,
  tenantId: Uuid,
  rank: string | null,
): Person {
  return {
    id,
    tenant_id: tenantId,
    name,
    role,
    roles: [role],
    rank,
    title: '',
    active: false,
    created_at: '',
    updated_at: '',
    updated_by: null,
    deleted_at: null,
  };
}

// ---------------------------------------------------------------- who may change it

/** Sign-in roles that may change the staff register and assign project crew (decision 422). */
export const STAFF_REGISTER_EDITORS = ['tenant_admin', 'manager', 'office_staff'] as const;

export function canEditStaffRegister(roles: readonly string[]): boolean {
  return roles.some((r) => (STAFF_REGISTER_EDITORS as readonly string[]).includes(r));
}

/**
 * Sign-in roles that may delete a person from the register and restore them: tenant admins and managers (decision 601,
 * Q528 B). Mirrored by public.person_delete and public.person_restore.
 */
export const STAFF_REGISTER_DELETERS = ['tenant_admin', 'manager'] as const;

export function canDeleteStaff(roles: readonly string[]): boolean {
  return roles.some((r) => (STAFF_REGISTER_DELETERS as readonly string[]).includes(r));
}

export function canAssignProjectCrew(roles: readonly string[]): boolean {
  return canEditStaffRegister(roles);
}

/**
 * What Teslyn Field may do with the register of the open project: a project set up offline (no organisation yet)
 * keeps its own list and edits it; a linked project follows the signed-in roles in its organisation, which are
 * `null` until this laptop has them ('unknown': shown read-only, with the reason).
 */
export function staffRegisterAccess(
  roles: readonly string[] | null,
  localWorkspace: boolean,
): 'edit' | 'read' | 'unknown' {
  if (localWorkspace) return 'edit';
  if (roles === null) return 'unknown';
  return canEditStaffRegister(roles) ? 'edit' : 'read';
}

// ---------------------------------------------------------------------------------------------- added on site (N11)

/** The register roles field crew may give a person they add on site (decision 471: a relief pilot, say). */
export const ON_SITE_ROLES: readonly RegisterRole[] = ['pilot', 'operator'];

/**
 * Whether the signed-in person adds people on site rather than keeping the register (decision 471, Q412 B): field
 * crew who do not also keep it. Their additions are marked "added on site" for the office to check.
 */
export function addsPeopleOnSite(roles: readonly string[] | null): boolean {
  return roles !== null && roles.includes('field_crew') && !canEditStaffRegister(roles);
}

/** Whether a person still waits for the office to check them (added on site, not checked yet). */
export function addedOnSite(p: Pick<Person, 'added_on_site'>): boolean {
  return p.added_on_site === true;
}
